import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Sparkles, MapPin, Phone, Globe } from 'lucide-react';
import { publicApi, resolveImage } from '../api';
import { useFetch } from '../useFetch';
import { useLang } from '../i18n';

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return src.startsWith('/') ? `${base}${src}` : `${base}/${src}`;
};

export default function ItineraryDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const { data: it, loading, error } = useFetch(`itinerary:${slug}`, () => publicApi.itinerary(slug), [slug]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gray-400" size={40} /></div>;
  if (error || !it) {
    return (
      <div className="max-w-4xl mx-auto py-24 px-6 text-center">
        <h1 className="text-3xl font-bold text-[color:var(--yup-primary)]">404</h1>
        <p className="mt-3 text-gray-600">Itinerario non trovato</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-6 text-[color:var(--yup-primary)] font-semibold">
          <ArrowLeft size={16} /> {t('backHome')}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`itinerary-detail-${slug}`}>
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="relative w-full min-h-[45vh] md:min-h-[55vh] flex items-end">
          {it.cover_image && (
            <img src={resolveImg(it.cover_image)} alt={it.title} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 pb-12 pt-24 text-white w-full">
            {it.sponsored && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-black mb-4"
                    style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
                <Sparkles size={10}/> Sponsored Editorial
              </span>
            )}
            <h1 className="text-4xl md:text-6xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {it.title}
            </h1>
            {it.subtitle && <p className="mt-4 text-white/80 text-lg max-w-2xl">{it.subtitle}</p>}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-6 py-14 text-white/85 leading-relaxed">
        {it.body_md && (
          <article className="prose prose-invert prose-headings:text-white prose-p:text-white/80 prose-a:text-[color:var(--yup-primary)] whitespace-pre-wrap text-base md:text-lg">
            {it.body_md}
          </article>
        )}
      </section>

      {/* Linked activities */}
      {it.activities && it.activities.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-20">
          <h2 className="text-xs tracking-[0.3em] uppercase text-[color:var(--yup-primary)] font-semibold mb-6">Le tappe</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {it.activities.map((a, i) => (
              <div key={a.id} className="flex gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.03] hover:border-[color:var(--yup-primary)]/40 transition">
                <div className="w-8 h-8 rounded-full bg-[color:var(--yup-primary)] text-black font-bold flex items-center justify-center flex-shrink-0">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold">{a.name}</h3>
                  {a.address && <div className="text-white/50 text-xs mt-0.5 flex items-center gap-1"><MapPin size={11}/>{a.address}</div>}
                  <div className="flex gap-2 mt-2">
                    {a.phone && <a href={`tel:${a.phone}`} className="text-[10px] px-2 py-1 rounded-full bg-[color:var(--yup-primary)] text-black font-bold inline-flex items-center gap-1"><Phone size={10}/> Chiama</a>}
                    {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded-full border border-white/20 text-white/85 inline-flex items-center gap-1"><Globe size={10}/> Sito</a>}
                  </div>
                </div>
                {a.image && <img src={resolveImage(a.image)} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0"/>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
